import React, { useState, useEffect, useContext } from "react";
import AddVendor from "../components/AddVendor";
import { can, Permissions } from "../auth/authorization";
import AuthContext from "../AuthContext";
import { apiFetch } from "../api/client";
import { useNotification } from "../components/Notifications";

function Store() {
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [stores, setAllStores] = useState([]);
  const authContext = useContext(AuthContext);
  const notify = useNotification();

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetching all stores data
  const fetchData = async () => {
    try {
      const res = await apiFetch(`/api/vendor`);
      if (!res.ok) return;
      const data = await res.json();
      const arr = Array.isArray(data) ? data : (data?.data || data?.Data || []);
      setAllStores(arr || []);
    } catch (err) {
      console.log(err);
      notify("error", { title: "Error", text: "Could not load vendors." });
    }
  };

  const modalSetting = () => setShowModal(!showModal);

  const handleEdit = (vendor) => {
    setSelectedVendor(vendor);
    setShowEditModal(true);
  };

  const handleDelete = (vendor) => {
    setSelectedVendor(vendor);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      // attempt backend delete if vendorId present
      if (selectedVendor && (selectedVendor.vendorId || selectedVendor.id)) {
        const id = selectedVendor.vendorId || selectedVendor.id;
        const res = await apiFetch(`/api/vendor/${id}`, { method: "DELETE" });
        if (!res.ok) {
          const parsed = await res.json().catch(() => null);
          notify("error", { title: "Delete failed", text: parsed?.message || "Could not delete vendor." });
          setShowDeleteModal(false);
          return;
        }
      }
      setAllStores(stores.filter((store) => (store.vendorId || store.id) !== (selectedVendor.vendorId || selectedVendor.id)));
      setShowDeleteModal(false);
      notify("success", { title: "Deleted", text: "Vendor removed." });
    } catch (err) {
      console.log(err);
      notify("error", { title: "Error", text: "Could not delete vendor." });
    }
  };

  const handleEditSave = (e) => {
    e.preventDefault();
    setAllStores((prev) =>
      prev.map((store) =>
        (store.vendorId || store.id) === (selectedVendor.vendorId || selectedVendor.id) ? selectedVendor : store
      )
    );
    setShowEditModal(false);
  };

  return (
    <div className="col-span-12 lg:col-span-10 flex justify-center">
      <div className="flex flex-col gap-5 w-11/12 border-2 bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center">
          <span className="font-bold text-lg text-gray-800">Manage Vendors</span>
          {can(Permissions.Add) && (
            <button
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 text-sm rounded-md"
              onClick={modalSetting}
            >
              + Add Vendor
            </button>
          )}
        </div>

        {showModal && can(Permissions.Add) && <AddVendor />}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {stores.map((element) => (
            <div
              key={element.vendorId || element.id}
              className="bg-white rounded-lg shadow-md p-5 flex flex-col gap-3 border hover:shadow-lg transition-shadow"
            >
              <h3 className="font-bold text-lg text-gray-800">{element.vendorName || element.name}</h3>
              <div className="text-gray-600 text-sm">📞 {element.phone || element.phoneNumber}</div>
                <div className="flex justify-between mt-4">
                {can(Permissions.Edit) && (
                  <button
                    onClick={() => handleEdit(element)}
                    className="px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600"
                  >
                    Edit
                  </button>
                )}
                {can(Permissions.Delete) && (
                  <button
                    onClick={() => handleDelete(element)}
                    className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedVendor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">Edit Vendor</h2>
            <form onSubmit={handleEditSave}>
              <input
                type="text"
                className="border w-full mb-2 p-2 rounded"
                value={selectedVendor.vendorName || selectedVendor.name}
                onChange={(e) =>
                  setSelectedVendor({ ...selectedVendor, vendorName: e.target.value, name: e.target.value })
                }
                placeholder="Vendor Name"
              />
              <input
                type="text"
                className="border w-full mb-2 p-2 rounded"
                value={selectedVendor.phone || selectedVendor.phoneNumber}
                onChange={(e) =>
                  setSelectedVendor({ ...selectedVendor, phone: e.target.value, phoneNumber: e.target.value })
                }
                placeholder="Phone"
              />
              <div className="flex justify-end gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="bg-gray-300 px-3 py-1 rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-3 py-1 rounded"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedVendor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-sm">
            <h2 className="text-lg font-semibold mb-4 text-red-600">Confirm Delete</h2>
            <p>
              Are you sure you want to delete <strong>{selectedVendor.vendorName || selectedVendor.name}</strong>?
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="bg-gray-300 px-3 py-1 rounded"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="bg-red-600 text-white px-3 py-1 rounded"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Store;
